/* eslint-disable react/no-multi-comp */
import React from 'react';
import {
  Button,
  Card,
  CardBody,
  Input,
} from 'reactstrap';
import CustomTabs from 'components/CustomTabs';
import { FiArrowLeft, FiClock, FiEdit2, FiCheck, FiX } from 'react-icons/fi';

const DetailCard = ({ title, children, actions = null, className = '' }) => (
  <Card className={`crm-lead-page__detail-card ${className}`}>
    <CardBody>
      <div className="crm-lead-page__section-head">
        <h5>{title}</h5>
        {actions}
      </div>
      <div className="crm-lead-page__card-content">{children}</div>
    </CardBody>
  </Card>
);

const UpdateLead = ({
  leadDetail,
  selectedLead,
  stagedEdits,
  inlineEditKey,
  inlineEditValue,
  isUpdatingLead,
  activeDetailTab,
  attachmentInputRef,
  attachmentName,
  detailTabs,
  timelineItems,
  detailGroups,
  leadInformationLeftFields,
  leadInformationRightFields,
  detailCardTitles,
  onBackToList,
  onUpdateStagedEdits,
  onDetailTabChange,
  onAttachmentChooseFile,
  onAttachmentFileChange,
  onNoteChange,
  onNoteSave,
  onNoteCancel,
  renderDetailFieldBound,
  formatDisplayTime,
}) => {
  const renderSectionGrid = (group) => {
    const columnsInGroup = Array.from({ length: group.columns }, (_, index) =>
      group.sections[index] || [],
    );

    return (
      <div className="crm-lead-page__detail-grid">
        {columnsInGroup.map((columnFields, columnIndex) => {
          const columnKey =
            columnFields.map((field) => field.key).join('-') ||
            `${group.title}-${columnIndex}`;

          return (
            <div className="crm-lead-page__detail-column" key={columnKey}>
              {columnFields.map((field) => (
                field.type === 'select' ? (
                  renderDetailFieldBound(
                    field.label,
                    field.key,
                    leadDetail?.[field.key],
                    'select',
                    field.options.map((option) => ({ label: option, value: option })),
                  )
                ) : (
                  renderDetailFieldBound(field.label, field.key, leadDetail?.[field.key])
                )
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  const renderLeadInformationSection = () => (
    <Card className="crm-lead-page__detail-card">
      <CardBody>
        <div className="crm-lead-page__section-head crm-lead-page__section-head--tight">
          <h5>Lead Information</h5>
        </div>
        <div className="crm-lead-page__card-content crm-lead-page__card-content--lead-info">
          <div className="crm-lead-page__lead-info-grid">
            <div className="crm-lead-page__lead-info-column">
              {leadInformationLeftFields.map((field) => (
                renderDetailFieldBound(field.label, field.key, leadDetail?.[field.key])
              ))}
            </div>
            <div className="crm-lead-page__lead-info-column">
              {leadInformationRightFields.map((field) => {
                if (field.type === 'select') {
                  return renderDetailFieldBound(
                    field.label,
                    field.key,
                    leadDetail?.[field.key],
                    'select',
                    field.options.map((option) => ({ label: option, value: option })),
                  );
                }

                return renderDetailFieldBound(field.label, field.key, leadDetail?.[field.key]);
              })}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  const renderOverview = () => (
    <div className="crm-lead-page__overview">
      <Card className="crm-lead-page__summary-card">
        <CardBody className="crm-lead-page__summary-card-body">
          <div className="crm-lead-page__summary-strip">
            {renderDetailFieldBound('Lead Owner', 'leadOwner', leadDetail.leadOwner)}
            {renderDetailFieldBound('Email', 'email', leadDetail.email)}
          </div>
        </CardBody>
      </Card>

      <DetailCard title="Next Action">
        <div className="crm-lead-page__next-action">
          <span className="crm-lead-page__next-action-badge">TODAY</span>
          <span className="crm-lead-page__next-action-text">
            New Lead assigned notification
          </span>
        </div>
      </DetailCard>

      {renderLeadInformationSection()}

      {detailGroups.map((group) => (
        <DetailCard title={group.title} key={group.title}>
          {renderSectionGrid(group)}
        </DetailCard>
      ))}

      <DetailCard title={detailCardTitles.notes}>
        <div className="crm-lead-page__notes-toolbar">
          <Button type="button" color="link" className="crm-lead-page__notes-sort">
            Recent Last
          </Button>
        </div>
        <div className="d-flex align-items-center gap-1 w-100">
          <Input
            className="crm-lead-page__note-input flex-grow-1"
            placeholder="Add a note"
            value={stagedEdits.notes !== undefined ? stagedEdits.notes : (selectedLead?.notes || '')}
            onChange={onNoteChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onNoteSave();
              if (e.key === 'Escape') onNoteCancel();
            }}
          />
          {stagedEdits.notes !== undefined && (
            <>
              <Button color="link" className="p-0 text-success ms-1 flex-shrink-0" onClick={onNoteSave}>
                <FiCheck size={18} />
              </Button>
              <Button color="link" className="p-0 text-danger ms-1 flex-shrink-0" onClick={onNoteCancel}>
                <FiX size={18} />
              </Button>
            </>
          )}
        </div>
      </DetailCard>

      <DetailCard title={detailCardTitles.attachments}>
        <div className="crm-lead-page__attachment-wrap">
          <input
            ref={attachmentInputRef}
            type="file"
            className="d-none"
            onChange={onAttachmentFileChange}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
          />
          <label className="form-label crm-lead-page__attachment-label">
            Attachment File
          </label>
          <div className="crm-lead-page__attachment-row">
            <Button
              type="button"
              color="light"
              className="crm-lead-page__choose-file-btn border"
              onClick={onAttachmentChooseFile}
            >
              Choose File
            </Button>
            <span className="crm-lead-page__attachment-name" title={stagedEdits.attachment !== undefined ? stagedEdits.attachment : (selectedLead?.attachment || attachmentName)}>
              {(() => {
                const val = stagedEdits.attachment !== undefined ? stagedEdits.attachment : (selectedLead?.attachment || attachmentName);
                if (val && val.startsWith('http')) {
                  return (
                    <a href={val} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                      {val}
                    </a>
                  );
                }
                return val || 'No file chosen';
              })()}
            </span>
          </div>
        </div>
      </DetailCard>
    </div>
  );

  // Timeline now directly shows the "History" content — no sub-tabs
  // (Interactions tab removed as requested).
  const renderTimeline = () => (
    <Card className="crm-lead-page__timeline-card">
      <CardBody>
        <div className="crm-lead-page__timeline-shell">
          <div className="crm-lead-page__timeline-history">
            <div className="crm-lead-page__timeline-date">Jun 22, 2026</div>

            <div className="crm-lead-page__timeline-list">
              {timelineItems.map((item) => (
                <div className="crm-lead-page__timeline-item" key={`${item.time}-${item.title}-${item.meta}`}>
                  <div className="crm-lead-page__timeline-time">{item.time}</div>
                  <div className="crm-lead-page__timeline-marker">
                    <FiEdit2 size={11} />
                  </div>
                  <div className="crm-lead-page__timeline-content">
                    <div className="crm-lead-page__timeline-text">
                      <strong>{item.title}</strong>
                      {item.lines.map((line) => (
                        <span key={line} className="crm-lead-page__timeline-line">
                          {line}
                        </span>
                      ))}
                    </div>
                    <div className="crm-lead-page__timeline-meta">{item.meta}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  return (
    <div className="crm-lead-page__detail-view">
      <div className="crm-lead-page__detail-topbar">
        <div className="crm-lead-page__detail-heading">
          <Button
            color="link"
            className="crm-lead-page__back-btn"
            onClick={onBackToList}
          >
            <FiArrowLeft size={18} />
          </Button>
          <div className="crm-lead-page__detail-title-wrap">
            <h4 className="crm-lead-page__detail-title mb-0">{leadDetail.leadName}</h4>
          </div>
        </div>
        {(Object.keys(stagedEdits).length > 0 || inlineEditKey) && (
          <div className="ms-auto pe-3">
            <Button
              color="primary"
              onClick={() => onUpdateStagedEdits(inlineEditKey, inlineEditValue)}
              disabled={isUpdatingLead}
              className="d-flex align-items-center gap-1"
              style={{ borderRadius: 6 }}
            >
              {isUpdatingLead ? 'Updating...' : `Update`}
            </Button>
          </div>
        )}
      </div>

      <div className="crm-lead-page__detail-tabs">
        <CustomTabs
          tabs={detailTabs}
          selectedTab={activeDetailTab}
          onTabChange={onDetailTabChange}
          className="mb-0"
        />
        <div className="crm-lead-page__last-update crm-lead-page__last-update--detail">
          <FiClock size={14} />
          <span>Last Update : {formatDisplayTime(selectedLead?.modifiedTime)}</span>
        </div>
      </div>

      {activeDetailTab === 'Overview' ? renderOverview() : renderTimeline()}
    </div>
  );
};

export default UpdateLead;